import React from 'react';
import { RequirementsProps } from '../../../tools/context';
import { useWindowSize } from '../../../tools/hooks';
import { useTranslation } from '../../../tools/i18n';
import { CustomDataStore } from '../../../tools/providersAPI';
import ButtonV2 from '../../ButtonV2';
import LeafletMapLazy from '../../LeafletMap/LeafletMapLazy';
import ShopV2Card from '../../ShopV2Card';
import { DetailShopV2Props } from '../Detail';
import IconList from '../IconList';
import ManagementStoreV2 from '../Management';

interface HeroShopV2Props {
  requirements: RequirementsProps;
  title: string;
  imageUrls: string[];
  seller: { [key: string]: any };
  data: { [key: string]: any };
  customData?: CustomDataStore;
}

const HeroShopV2: React.FC<HeroShopV2Props> = ({
  requirements,
  title,
  imageUrls,
  seller,
  data,
  customData,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { contactEmail: email } = seller;
  const lat = customData?.store?.address?.latitude ?? seller?.latitude ?? data?.latitude;
  const lng = customData?.store?.address?.longitude ?? seller?.longitude ?? data;

  const address = customData?.store?.address;
  const city = address?.city || data?.city;
  const cityLine = [address?.postalCode, city].filter(Boolean).join(' ');
  const addressLine = [address?.address1, cityLine].filter(Boolean).join(', ');
  const howToGoLink =
    lat && lng ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}` : undefined;
  const websiteLink = customData?.store?.website;

  const details: DetailShopV2Props[] = [
    {
      icon: 'mapPin',
      title: addressLine || city,
      link: howToGoLink
        ? {
            href: howToGoLink,
            target: '_blank',
          }
        : undefined,
    },
  ];

  const management = customData?.store?.management;

  const getSocialNetwork = (networkName: string) => {
    const socialNetworkGroup = customData?.groups?.find(
      (group) => group.title === '{{t:socialNetworks}}'
    );

    if (!socialNetworkGroup) return null;

    const matchingItem = socialNetworkGroup.items.find((item) =>
      // @ts-ignore
      item.details.some((detail) => detail.title.toLowerCase() === networkName.toLowerCase())
    );

    return (
      // @ts-ignore
      matchingItem?.details.find(
        (detail: DetailShopV2Props) => detail.title.toLowerCase() === networkName.toLowerCase()
      )?.value || null
    );
  };

  return (
    <div className="HeroShopV2">
      <ShopV2Card imageUrls={imageUrls} title={title} details={details} h1Title={isMobile} />
      <div className="HeroShopV2__buttonsContainer">
        {websiteLink ? (
          <div className="HeroShopV2__primaryButtons">
            <ButtonV2
              className="HeroShopV2__websiteButton fiche-sit-website"
              text="Site internet"
              leftIcon="website"
              align="left"
              link={websiteLink}
              linkTarget="_blank"
            />
          </div>
        ) : null}
        <IconList
          title={t('shopV2.hero.contactTitle')}
          phone={customData?.store?.phone}
          email={email}
          facebook={getSocialNetwork('facebook')}
          instagram={getSocialNetwork('instagram')}
          googleMyBusiness={getSocialNetwork('google my business')}
        />
        {lat && lng ? (
          <LeafletMapLazy
            center={[lat, lng]}
            zoom={15}
            markers={[
              {
                position: [lat, lng],
                popup: customData?.store?.name ?? seller.name,
              },
            ]}
            className="HeroShopV2__map"
          />
        ) : null}
        {management ? (
          <ManagementStoreV2
            name={management.name}
            email={management.email}
            website={management.website}
            createdAt={management.createdAt}
            updatedAt={management.updatedAt}
            noContainer
            id={data?.apidae_id}
            url={requirements.href}
          />
        ) : null}
      </div>
    </div>
  );
};

export default HeroShopV2;
